import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { getUserToken } from './jsBridge';
import { LOCALES } from './config/i18n-config';
import { genTraceID } from './utils';

const baseURL = (() => {
  if (typeof window === 'undefined') {
    return `//api2.bybit.com`;
  }
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname.startsWith('dev')) {
    const devKey = 'web3_dev_api_host';
    if (sessionStorage.getItem(devKey)) {
      return sessionStorage.getItem(devKey);
    }
    return `https://api2.sbu-test-5.bybit.com`;
  }
  // http://api2.sbu-test-7.bybit.com
  // return `http://localhost:7080`;

  const domain = hostname.split('.').slice(1).join('.');
  if (/testnet/.test(hostname)) return `//api2-testnet.${domain}`;
  return `//api2.${domain}`;
  // if (/(\w+-test-\d+)/.test(hostname)) return `//api2.sbu-test-5.bybit.com`;
  // if (/bycloud/.test(hostname)) return `//api2.bycloud.org`;
  // return `//api2.bybit.com`;
})();

const timeout = 20000;

export const getLocaleByUrl = () => {
  if (typeof window === 'undefined') {
    return 'en';
  }
  const pathname = window.location.pathname;
  // if (window.$wujie) {
  //   pathname = window.$wujie.props?.initialRoute;
  // } else {
  //   pathname = window.location.pathname;
  // }

  const lang = pathname.split('/')[1] || 'en';
  const isExist = LOCALES.find((k) => k.value === lang);
  return isExist ? lang : 'en';
};

/** 请求结构体，不一致时axios统一处理成一致的 */
export type Response<T = unknown> = {
  code: number;
  success: boolean;
  message: string;
  result: T;
};

/** 转换请求数据 */
const transformData = (data: {
  error?: { code: number; message: string };
  [key: string]: unknown;
}) => {
  let response: Response = {
    code: 0,
    success: true,
    message: '',
    result: '',
  };
  // eslint-disable-next-line
  if (data.hasOwnProperty('jsonrpc') && data.hasOwnProperty('id')) {
    // 节点数据转换
    response = {
      code: data.error && data.error.code ? data.error.code : 0,
      success: !data.error,
      result: data.result || data.entries,
      message: data.error ? data.error.message : '',
    };
  } else if (data.data && data.timeCost) {
    /** APEX接口请求 */
    response = {
      code: 0,
      success: true,
      result: data.data,
      message: '',
    };
  } else {
    if (data.ret_code === undefined) {
      data.ret_code = data.retCode;
    }
    response = {
      code: data.ret_code as number,
      success: data.ret_code === 0 || !!data.entries || data.retCode === 0,
      message: (data.ret_msg || data.retMsg || data.error) as string,
      result: data.result || data.entries,
    };
  }

  if (!response.success && !response.message) {
    response.message = (data.error_message as string) || '';
  }
  return response;
};

export const Axios: AxiosInstance = axios.create({
  baseURL,
  timeout,
  withCredentials: true,
});

// 请求参数处理
Axios.interceptors.request.use(async (config) => {
  // console.log('axios Accept-Language', getLocaleByUrl());
  config.headers['Accept-Language'] = getLocaleByUrl();
  // config.headers.Usertoken =
  // 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE3MDcwMjExNjYsInVzZXJfaWQiOjM3NzkxOTksIm5vbmNlIjoiNDMxYzg3NWMiLCJnZW5fdHMiOjE3MDY3NjE5NjYsInAiOjMsImV4dCI6eyJTdGF0aW9uLVR5cGUiOiIiLCJtY3QiOiIxNzA2MDY2OTIxIiwic2lkIjoiQllCSVQifX0.UJwB_Ghbb3EjWrh1qmcW8nXxCJLp6iGeYR_njJIpsF4';

  const token = await getUserToken();
  if (token) {
    config.headers.usertoken = token;
  }

  // 非节点请求添加其他请求头参数
  if (
    !config.url?.includes('/web3/node/') &&
    !config.url?.includes('/apex/api/')
  ) {
    config.headers.traceparent = genTraceID();
  }

  return config;
});

// 响应数据处理
Axios.interceptors.response.use((response: AxiosResponse) => {
  return transformData(response.data) as unknown as AxiosResponse;
});

/** 导出get方法 */
export const get = <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<Response<T>> => {
  return Axios.get(url, config);
};

/** 导出post方法 */
export const post = <T>(
  url: string,
  data?: object,
  config?: AxiosRequestConfig
): Promise<Response<T>> => {
  return Axios.post(url, data, config);
};

export async function getResultFromResponse<T>(
  item: Promise<Response<T>> | Response<T>,
  onError?: (res: Response<T>) => void
) {
  const res = await item;
  if (res.success) {
    return res.result;
  }
  if (onError) {
    onError(res);
  }
  return Promise.reject(res);
}

export { baseURL as APIBaseURL };
